import React, { useState, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider, Button, Paper } from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const StudentPrint = () => {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);

  useEffect(() => {
    // Get student data from sessionStorage
    const studentData = sessionStorage.getItem('printStudent');
    if (studentData) {
      setStudent(JSON.parse(studentData));
    } else {
      navigate('/admin/students');
    }
  }, [navigate]);

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  if (!student) {
    return <Box sx={{ p: 4 }}>Loading...</Box>;
  }

  return (
    <Box sx={{ p: 4, maxWidth: '210mm', margin: '0 auto', backgroundColor: '#fff' }}>
      {/* Print Button - Hidden in print */}
      <Box className="no-print" sx={{ mb: 3, textAlign: 'right' }}>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
          Print
        </Button>
      </Box>

      {/* Header - BSS IT Mission */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
          BSS INFORMATION TECHNOLOGY MISSION
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          (Bharat Sevak Samaj, National Development Agency)
        </Typography>
        <Typography variant="body2">
          Established in 1952 by Planning Commission, Govt. of India
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          National Head Office: Nehru Sadan, 5-Institutional Area, Lodhi Road, New Delhi-110003
        </Typography>
        <Typography variant="body2">
          Ph: 011-24616652, 24631552 | Email: head@bssnl.in | Web: www.bssnl.in
        </Typography>
      </Box>

      <Divider sx={{ my: 2, borderWidth: 2 }} />

      {/* Date */}
      <Box sx={{ textAlign: 'right', mb: 2 }}>
        <Typography variant="body2">
          <strong>Date:</strong> {formatDate(new Date())}
        </Typography>
      </Box>

      {/* Student Application Header */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, textDecoration: 'underline' }}>
          STUDENT APPLICATION FORM
        </Typography>
      </Box>

      {/* Form Number and Centre */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1">
          <strong>Form Number:</strong> {student.formNumber}
        </Typography>
        <Typography variant="body1">
          <strong>Centre Name:</strong> {student.centre?.name} ({student.centre?.code})
        </Typography>
        <Typography variant="body1">
          <strong>Course Name:</strong> {student.course?.name} ({student.course?.code})
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Personal Information */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, backgroundColor: '#f5f5f5', p: 1 }}>
          1. Personal Information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Name (as per TC):</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{student.personalInfo?.nameAsPerTC}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Date of Birth:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDate(student.personalInfo?.dateOfBirth)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Gender:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{student.personalInfo?.gender}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Phone:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{student.personalInfo?.phone}</Typography>
          </Box>
          {student.personalInfo?.email && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">Email:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{student.personalInfo?.email}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Addresses */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, backgroundColor: '#f5f5f5', p: 1 }}>
          2. Address Details
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Permanent Address:</Typography>
            <Typography variant="body2">
              {student.permanentAddress?.street}<br />
              {student.permanentAddress?.city}, {student.permanentAddress?.district}<br />
              {student.permanentAddress?.state} - {student.permanentAddress?.pincode}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Communication Address:</Typography>
            <Typography variant="body2">
              {student.communicationAddress?.street}<br />
              {student.communicationAddress?.city}, {student.communicationAddress?.district}<br />
              {student.communicationAddress?.state} - {student.communicationAddress?.pincode}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Father/Guardian */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, backgroundColor: '#f5f5f5', p: 1 }}>
          3. Father/Guardian Information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Name:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{student.fatherOrGuardian?.name}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Occupation:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{student.fatherOrGuardian?.occupation || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Phone:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{student.fatherOrGuardian?.phone || '-'}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Educational Qualifications */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, backgroundColor: '#f5f5f5', p: 1 }}>
          4. Educational Qualifications
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Examination</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>University/Board</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Year</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Class/Division</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Grade/%</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {student.educationalQualifications?.map((qual, index) => (
                <TableRow key={index}>
                  <TableCell>{qual.examination}</TableCell>
                  <TableCell>{qual.universityOrBoard}</TableCell>
                  <TableCell>{qual.yearOfPassing}</TableCell>
                  <TableCell>{qual.classOrDivision}</TableCell>
                  <TableCell>{qual.gradeOrPercentage}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Employment */}
      {student.employment?.isEmployed && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, backgroundColor: '#f5f5f5', p: 1 }}>
            5. Employment Details
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Employer:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{student.employment?.employerName}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Designation:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{student.employment?.designation}</Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Declarations */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, backgroundColor: '#f5f5f5', p: 1 }}>
          Declarations
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Applicant's Declaration:</Typography>
          <Typography variant="body2">
            I hereby declare that the information given above is true to the best of my knowledge and belief.
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Place: {student.declarations?.applicantDeclaration?.place || '___________'}</Typography>
            <Typography variant="body2">Date: {formatDate(student.declarations?.applicantDeclaration?.date)}</Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>Applicant's Signature: ___________________</Typography>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Parent/Guardian's Declaration:</Typography>
          <Typography variant="body2">
            I hereby give my consent for my son/daughter/ward to join this course and declare that the above information is correct.
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Place: {student.declarations?.parentGuardianDeclaration?.place || '___________'}</Typography>
            <Typography variant="body2">Date: {formatDate(student.declarations?.parentGuardianDeclaration?.date)}</Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>Parent/Guardian's Signature: ___________________</Typography>
        </Box>
      </Box>

      {/* Office Use Only */}
      <Box sx={{ mb: 3, border: '2px solid #000', p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          FOR OFFICE USE OF BSS IT MISSION ONLY
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Status: <strong>{student.status?.toUpperCase()}</strong>
        </Typography>
        {student.approvedAt && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            Approved Date: {formatDate(student.approvedAt)}
          </Typography>
        )}
        {student.rejectionReason && (
          <Typography variant="body2" sx={{ mb: 1, color: 'error.main' }}>
            Rejection Reason: {student.rejectionReason}
          </Typography>
        )}
        {student.isAlumni && (
          <Typography variant="body2" sx={{ mb: 1, color: 'primary.main' }}>
            Alumni Status: Converted on {formatDate(student.convertedToAlumniAt)}
          </Typography>
        )}
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2">Authorized Signature: ___________________</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>Date: ___________________</Typography>
        </Box>
      </Box>

      {/* Print CSS */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          @page {
            margin: 20mm;
            size: A4;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      `}</style>
    </Box>
  );
};

export default StudentPrint;
